import React from "react";
import { UncontrolledTooltip, Badge } from "reactstrap";
import {
  faCircleXmark,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import FormGenerator from "components/customs/form-generator/input";
import {
  getFormattedDateString,
  getFormattedNumber,
  hasPermission,
} from "utility/helpers";
import {
  StyledEditIcon,
  StyledEyeIcon,
  StyledTrashIcon,
  StyledSaveIcon,
  StyledFontAwesomeIcon,
  CellText,
} from "assets/js/Styles";
import { ACCOUNT_HOUSE_SPEAKER } from "utility/constants";
import styled from "styled-components";

const StyledBadge = styled(Badge)`
  background: ${({ theme }) => theme.colors.badgeBlue} !important;
`;

const StyledFormGenerator = styled(FormGenerator)`
  background: ${({ theme }) => theme.colors.white};
  height: unset;
  border-radius: 0;
  padding: 2px 10px !important;

  &[inputmode="numeric"] {
    height: 34px;
  }

  &.flatpickr-input {
    height: 34px;
    border: 2px solid ${({ theme }) => theme.colors.black100};
    padding: 2px 10px;
  }
`;

export const defaultColumns = [
  {
    name: "",
    selector: (row) => row?.id,
    sortable: false,
    width: "50px",
    cell: (row, index) => <div>{index + 1}</div>,
  },
  {
    name: "Agency",
    selector: (row) => row?.name,
    identifier: "name",
    sortable: true,
    cell: ({ name, acronym }) => <div>{`${name} (${acronym})`}</div>,
  },
  {
    name: "Recommended Amount",
    selector: (row) => row?.budgetAllocation,
    identifier: "budgetAllocation",
    sortable: true,
    id: "budget",
    cell: ({ budgetAllocation }) => (
      <div>{`PHP ${Number(budgetAllocation)?.toLocaleString("en-US")}`}</div>
    ),
  },
];

export const newColumns = ({
  editingRowId,
  setEditingRowId,
  tableOption,
  setTableOption,
  selectedProject,
  handleDeleteRow,
  handleSaveClicked,
  handleCancelClicked,
  navigate,
  role,
}) => [
  {
    name: "",
    selector: null,
    sortable: false,
    id: "highlight",
    width: "5px",
    cell: () => null,
  },
  {
    name: "",
    selector: (row) => row?.pendingCount,
    sortable: false,
    width: "50px",
    id: "badge",
    cell: ({ pendingCount, requestedCount }) => {
      if (role !== ACCOUNT_HOUSE_SPEAKER) {
        const safePending = Number(pendingCount) || 0;
        const safeRequested = Number(requestedCount) || 0;
        const totalCount = safePending + safeRequested;

        return totalCount !== 0 ? (
          <StyledBadge pill>{totalCount}</StyledBadge>
        ) : null;
      }

      return null;
    },
  },
  {
    name: "Program",
    selector: (row) => row?.name,
    identifier: "name",
    grow: 2,
    sortable: true,
    cell: (row) =>
      editingRowId !== row.id ? (
        <CellText>{row?.name}</CellText>
      ) : (
        <StyledFormGenerator
          type="text"
          onChange={(e) => {
            const { value } = e.target;

            const newData = [...tableOption.data];
            const idx = newData.findIndex((item) => item.id === row.id);

            newData[idx] = {
              ...row,
              name: value,
            };

            setTableOption({
              ...tableOption,
              data: newData,
            });
          }}
          value={row?.name}
        />
      ),
  },
  {
    name: "Date",
    selector: (row) => row?.implemented,
    identifier: "implemented",
    grow: 2,
    sortable: true,
    cell: (row) =>
      editingRowId !== row.id ? (
        <CellText>
          {getFormattedDateString(row?.implemented, "MMM-yyyy")}
        </CellText>
      ) : (
        <StyledFormGenerator
          type="date"
          value={row?.implemented}
          dateFormat="M-Y"
          onChange={(date) => {
            const newData = [...tableOption.data];
            const idx = newData.findIndex((item) => item.id === row.id);

            newData[idx] = {
              ...row,
              implemented: date[0],
            };

            setTableOption({
              ...tableOption,
              data: newData,
            });
          }}
        />
      ),
  },
  {
    name: "Recommended Amount",
    selector: (row) => row?.budgetAllocation,
    identifier: "budgetAllocation",
    grow: 2,
    sortable: true,
    cell: (row) => (
      <CellText>{getFormattedNumber(row?.budgetAllocation)}</CellText>
    ),
  },
  {
    name: "Recommended Budget",
    selector: (row) => row?.recommendedBudget,
    identifier: "recommendedBudget",
    grow: 2,
    sortable: true,
    cell: (row) =>
      editingRowId !== row.id ? (
        <CellText>{getFormattedNumber(row?.recommendedBudget)}</CellText>
      ) : (
        <StyledFormGenerator
          type="number"
          value={Number(row?.recommendedBudget) || 0}
          onChange={(value) => {
            const { floatValue } = value;
            const newData = [...tableOption.data];
            const idx = newData.findIndex((item) => item.id === row.id);

            newData[idx] = {
              ...row,
              recommendedBudget: floatValue,
            };

            setTableOption({
              ...tableOption,
              data: newData,
            });
          }}
        />
      ),
  },
  {
    name: "Action",
    selector: (row) => row?.id,
    grow: 2,
    sortable: false,
    width: "100px",
    style: { cursor: "pointer" },
    center: true,
    id: "action",
    cell: (row) => {
      return (
        <>
          {editingRowId !== row.id && hasPermission("project-update") && (
            <>
              <StyledEditIcon
                size={16}
                id={`edit-row--${row.id}`}
                onClick={() => {
                  setEditingRowId(row.id);
                }}
              />
              <UncontrolledTooltip
                placement="left"
                target={`edit-row--${row.id}`}
              >
                {"Edit"}
              </UncontrolledTooltip>
            </>
          )}
          {editingRowId !== row.id && hasPermission("project-view") && (
            <>
              <StyledEyeIcon
                size={16}
                onClick={() =>
                  navigate(`/projects/${selectedProject?.ID}/program/${row.id}`)
                }
                id={`view-row--${row.id}`}
              />
              <UncontrolledTooltip
                placement="top"
                target={`view-row--${row.id}`}
              >
                {"View"}
              </UncontrolledTooltip>
            </>
          )}
          {editingRowId !== row.id && hasPermission("project-delete") && (
            <>
              <StyledTrashIcon
                size={16}
                onClick={() => handleDeleteRow(row)}
                id={`delete-row--${row.id}`}
              />
              <UncontrolledTooltip
                placement="right"
                target={`delete-row--${row.id}`}
              >
                {"Delete"}
              </UncontrolledTooltip>
            </>
          )}
          {editingRowId === row.id && hasPermission("project-view") && (
            <>
              <StyledSaveIcon
                id={`save-row--${row.id}`}
                size="xl"
                icon={faCircleCheck}
                onClick={() => handleSaveClicked(row)}
              />

              <UncontrolledTooltip
                placement="left"
                target={`save-row--${row.id}`}
              >
                {"Save"}
              </UncontrolledTooltip>

              <StyledFontAwesomeIcon
                id={`cancel-row--${row.id}`}
                size="xl"
                icon={faCircleXmark}
                onClick={() => handleCancelClicked(row.id)}
              />
              <UncontrolledTooltip
                placement="right"
                target={`cancel-row--${row.id}`}
              >
                {"Cancel"}
              </UncontrolledTooltip>
            </>
          )}
        </>
      );
    },
  },
];
